import React, { useMemo } from "react";
import { View, StyleSheet } from "react-native";
import { useNavigation } from "@react-navigation/native";
import type { NativeStackNavigationProp } from "@react-navigation/native-stack";
import { FileText, Pill } from "lucide-react-native";
import { Text } from "@src/packages/medical-history/components/MedicalHistoryText";
import {
  QuestionLayout,
  IconInput,
} from "@src/packages/medical-history/components";
import { CardSurface, DateField } from "@louise/moon-ui";
import { useTranslations } from "@src/packages/i18n";
import { useDiseaseHistory } from "../../DiseaseHistoryContext";
import type { DiseaseDetail } from "../../DiseaseHistoryContext";
import type { DiseaseHistoryStackParamList } from "../../DiseaseHistoryNavigator";
import { useFlowProgress } from "../../useFlowProgress";
import {
  DISEASES_BY_CATEGORY,
  CATEGORY_LABEL_KEY_MAP,
  type DiseaseCategoryType,
} from "../options";
import { useMedicalHistoryTheme } from "../../../../theme/MedicalHistoryTheme";

function getDiseaseLabelKey(
  category: DiseaseCategoryType,
  disease: string,
): string | null {
  const options = DISEASES_BY_CATEGORY[category];
  return options?.find((o) => o.id === disease)?.labelKey ?? null;
}

export function DiseaseDetailsScreen() {
  const t = useTranslations();
  const { state, updateState, save } = useDiseaseHistory();
  const navigation =
    useNavigation<NativeStackNavigationProp<DiseaseHistoryStackParamList>>();
  const progress = useFlowProgress("DiseaseDetails");
  const theme = useMedicalHistoryTheme();

  const updateDisease = (index: number, updates: Partial<DiseaseDetail>) => {
    const updated = state.diseases.map((d, i) =>
      i === index ? { ...d, ...updates } : d,
    );
    updateState({ diseases: updated });
  };

  // Group diseases by category, preserving order
  const groupedDiseases = useMemo(() => {
    const groups: {
      category: DiseaseCategoryType;
      labelKey: string;
      entries: { disease: DiseaseDetail; globalIndex: number }[];
    }[] = [];
    const seenCategories = new Map<string, number>();

    state.diseases.forEach((disease, globalIndex) => {
      const existing = seenCategories.get(disease.category);
      if (existing !== undefined) {
        groups[existing]!.entries.push({ disease, globalIndex });
      } else {
        seenCategories.set(disease.category, groups.length);
        groups.push({
          category: disease.category,
          labelKey:
            CATEGORY_LABEL_KEY_MAP[disease.category] ?? disease.category,
          entries: [{ disease, globalIndex }],
        });
      }
    });

    return groups;
  }, [state.diseases]);

  const handleSave = async () => {
    await save();
    const currentIdx = state.navigationStack.indexOf("DiseaseDetails");
    const nextRoute = state.navigationStack[currentIdx + 1];
    if (nextRoute) {
      navigation.navigate(nextRoute);
    }
  };

  return (
    <QuestionLayout
      title={t("med_history.medical.disease_history.disease_details.title")}
      subtitle={t(
        "med_history.medical.disease_history.disease_details.subtitle",
      )}
      onBack={() => navigation.goBack()}
      progress={progress}
      onSave={handleSave}
      saveDisabled={false}
      saveLabel={t("med_history.medical.shared.next")}
    >
      <View style={styles.container}>
        {groupedDiseases.map(({ category, labelKey, entries }) => (
          <View key={category}>
            {groupedDiseases.length > 1 && (
              <Text
                style={{
                  color: theme.textMuted,
                  fontSize: 14,
                  fontWeight: "500",
                  letterSpacing: 0.5,
                  marginBottom: 12,
                  paddingLeft: 4,
                }}
              >
                {t(labelKey)}
              </Text>
            )}

            {entries.map(({ disease, globalIndex }) => {
              const diseaseLabelKey = getDiseaseLabelKey(
                category,
                disease.disease,
              );
              return (
                <CardSurface key={globalIndex} style={styles.detailCard}>
                  <View style={styles.cardHeader}>
                    <Text style={[styles.cardTitle, { color: theme.text }]}>
                      {disease.disease === "other"
                        ? t(
                            "med_history.medical.disease_history.disease_details.other_section_label",
                          )
                        : diseaseLabelKey
                          ? t(diseaseLabelKey)
                          : disease.disease}
                    </Text>
                  </View>

                  {disease.disease === "other" && (
                    <IconInput
                      icon={FileText}
                      label={t(
                        "med_history.medical.disease_history.disease_details.other_name_label",
                      )}
                      placeholder={t(
                        "med_history.medical.disease_history.disease_details.other_name_placeholder",
                      )}
                      value={disease.other ?? ""}
                      onChangeText={(text) =>
                        updateDisease(globalIndex, {
                          other: text || null,
                        })
                      }
                    />
                  )}

                  <DateField
                    label={t(
                      "med_history.medical.disease_history.disease_details.diagnosis_date_label",
                    )}
                    value={disease.diagnosisDate ?? ""}
                    onChange={(iso) =>
                      updateDisease(globalIndex, {
                        diagnosisDate: iso || null,
                      })
                    }
                  />

                  <IconInput
                    icon={Pill}
                    label={t(
                      "med_history.medical.disease_history.disease_details.treatment_label",
                    )}
                    placeholder={t(
                      "med_history.medical.disease_history.disease_details.treatment_placeholder",
                    )}
                    value={disease.treatment ?? ""}
                    onChangeText={(text) =>
                      updateDisease(globalIndex, {
                        treatment: text || null,
                      })
                    }
                    multiline
                    numberOfLines={3}
                  />
                </CardSurface>
              );
            })}
          </View>
        ))}
      </View>
    </QuestionLayout>
  );
}

const styles = StyleSheet.create({
  container: {
    width: "100%",
    marginBottom: 40,
    gap: 24,
  },
  detailCard: {
    padding: 16,
    gap: 16,
    marginBottom: 16,
  },
  cardHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  cardTitle: {
    fontSize: 12,
    fontWeight: "600",
  },
});
